import {
  RequirementWorkflowType,
  type WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useCallback } from "react";

import { type WorkerRequirement } from "../Documents/api/types";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "./useGetRunBackgroundCheckOnBookingConfiguration";

export function useFilterBackgroundCheckRequirements() {
  const { isEnabled: isBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  /**
   * Filters out Checkr background check requirements based on status.
   *
   * When `filter.status` is specified, it filters out background checks that match
   * those statuses while keeping non-background-check requirements and background checks
   * with other statuses.
   *
   *
   * We display requirements (or requirement-related UI) in several places, for example:
   * - The blocking-requirements carousel in the shift bottom sheet
   * - Missing-requirements pills on the shift card in shift discovery
   * - The requirements card in the Documents page (Accounts section)
   * - Requirement pills inside the Documents page, etc.
   *
   * Background checks behave differently from other requirements, and therefore follow
   * their own display rules.
   *
   * With passive background checks (i.e., checks triggered at booking rather than before),
   * background checks are no longer considered blocking requirements. In fact, we do not
   * want to show them as requirements at all. So UI components use this helper to filter
   * background-check items out of the requirements list.
   *
   * Example usage: In the Documents page (WorkplaceSpecificDocuments), we use this
   * to filter out EXPIRING background checks from the incomplete documents list because
   * workers with run-background-check-on-booking enabled should see EXPIRING as APPROVED.
   * We don't want them to see that it's expiring since once they book a shift, the
   * background check will run automatically in the background.
   */
  const filterOutBackgroundCheckRequirements = useCallback(
    (params: {
      workerRequirements: WorkerRequirement[];
      filter: {
        status?: WorkerRequirementStatus[];
      };
    }): WorkerRequirement[] => {
      const { workerRequirements, filter } = params;

      if (!isBackgroundCheckOnBookingEnabled) {
        return workerRequirements;
      }

      return workerRequirements.filter((workerRequirement) => {
        if (
          workerRequirement.requirement?.attributes.requirementWorkflowType !==
          RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
        ) {
          return true;
        }

        // If status filter is specified, keep background checks that DON'T match the filter statuses.
        // This allows callers to exclude specific statuses (e.g., EXPIRING) while keeping others.
        if (isDefined(filter?.status)) {
          return !filter.status.includes(workerRequirement.status);
        }

        return false;
      });
    },
    [isBackgroundCheckOnBookingEnabled]
  );

  return {
    filterOutBackgroundCheckRequirements,
  };
}
